import React, { useState, useEffect } from "react";
import { Route, useHistory } from "react-router-dom";

import { commonMessages } from "infrastructure/localization";
import { ScreenContextProvider, Splash } from "ui/app";
import { DefaultLayout } from "ui/app/layout";
import { IAppRouteProps, AppRedirect } from "ui/app/navigation";
import { AuthorizedContent } from "ui/app/user-provider";
import { useUserContext, useLocalizedValues } from "ui/hooks";

interface IAppRouteContentProps {
  allowAnonymous?: boolean;
  authorizedContent: React.ReactElement;
  publicContent: React.ReactElement;
  signInPath?: string;
}

const AppRouteContent: React.FC<IAppRouteContentProps> = ({ allowAnonymous, authorizedContent, publicContent, signInPath = "/signin" }) => {
  const userContext = useUserContext();
  const history = useHistory();

  const localizedValues = useLocalizedValues(() => ({
    signInProcessing: commonMessages.info("signInProcessing"),
    signOutProcessing: commonMessages.info("signOutProcessing"),
    authorizationProcessing: commonMessages.info("authorizationProcessing")
  }));

  const [statusText, setStatusText] = useState(localizedValues.signInProcessing);

  const isSignInRoute = (path: string): boolean => {
    return path === signInPath;
  };

  useEffect(() => {
    if (userContext.isSigningOut()) {
      setStatusText(localizedValues.signOutProcessing);
    } else if (!userContext.isAuthorized()) {
      if (userContext.isAuthenticated()) {
        if (!userContext.hasError()) {
          if (!userContext.isAuthorizing()) {
            setStatusText(localizedValues.authorizationProcessing);
            userContext.authorize();
          }
        }
      } else if (!allowAnonymous) {
        if (!userContext.hasError()) {
          if (userContext.isRemoteAuthenticationRequired()) {
            userContext.initiateAuthentication(true, history.location.pathname);
          } else {
            userContext.initiateAuthentication(false);
          }
        }
      }
    }
  }, [userContext, allowAnonymous, localizedValues, history.location.pathname]);

  if (userContext.isAuthorized()) {
    return authorizedContent as React.ReactElement;
  } else if (userContext.hasError()) {
    if (isSignInRoute(history.location.pathname)) {
      return publicContent as React.ReactElement;
    } else {
      return <AppRedirect to={signInPath} />;
    }
  } else if (allowAnonymous) {
    return publicContent as React.ReactElement;
  }

  return <Splash statusText={statusText} />;
};

export const AppRoute: React.FC<IAppRouteProps> = ({ layout, component, requiredAuthorizationData, title, allowAnonymous, ...props }) => {
  const Component = component;
  const ScreenLayout = layout || DefaultLayout;
  const userContext = useUserContext();
  return (
    <Route
      {...props}
      render={renderProps => {
        return (
          <AppRouteContent
            authorizedContent={
              <ScreenContextProvider title={title}>
                <ScreenLayout>
                  <AuthorizedContent requiredAuthorizationData={requiredAuthorizationData} displayUnauthorizedMessage key={userContext.getTenantId()}>
                    <Component {...renderProps} />
                  </AuthorizedContent>
                </ScreenLayout>
              </ScreenContextProvider>
            }
            publicContent={
              <ScreenContextProvider title={title}>
                <ScreenLayout>
                  <Component {...renderProps} />
                </ScreenLayout>
              </ScreenContextProvider>
            }
            allowAnonymous={allowAnonymous}
          />
        );
      }}
    />
  );
};
